import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Slider } from 'primeng/slider';
import { Checkbox } from 'primeng/checkbox';
import { Textarea } from 'primeng/textarea';
import { ColorPicker } from 'primeng/colorpicker';
import { ButtonDirective } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { MediaPickerComponent } from '../media-picker/media-picker';
import { VarPickerComponent } from '../var-picker/var-picker';
import { AttributesEditorComponent } from '../attributes-editor/attributes-editor';
import { FiltersEditorComponent } from '../filters-editor/filters-editor';
import { FileCategory, JsonData } from '../../models';
import { ContentFilter, Placement, PlacementCoord } from '../../types/webout';
import { AeComp, cornerPinToButtonRect, parseAeCornerPin, toAeCornerPin, toWeboutDynamic, toWeboutStatic } from '../../utils/ae-converter';
import { cloneDeep } from '../../utils/clone';
import { humanizeEnum } from '../../utils/format';
import { EditorFilesService } from '../../../features/editor/services/editor-files.service';

@Component({
	selector: 'app-content-item-card',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, Select, InputText, InputNumber, Checkbox, Textarea, ColorPicker, ButtonDirective, Popover, Slider, MediaPickerComponent, VarPickerComponent, AttributesEditorComponent, FiltersEditorComponent],
	templateUrl: './content-item-card.html',
	styleUrl: './content-item-card.scss',
})
export class ContentItemCardComponent {
	private filesService = inject(EditorFilesService);
	 
	item = input.required<any>();
	index = input.required<number>();
	total = input.required<number>();
	aeComp = input<AeComp>({ fps: 25, width: 1920, height: 1080 });
	itemChange = output<JsonData>();
	requestRemove = output<void>();
	requestMoveUp = output<void>();
	requestMoveDown = output<void>();
	requestCopy = output<void>();

	protected expanded = signal(false);
	protected sourceExpanded = signal(true);
	protected placementExpanded = signal(true);
	protected filtersExpanded = signal(true);
	protected framesExpanded = signal(true);

	protected setFilters(filters: ContentFilter[]): void {
		this.patch(d => {
			delete d.filters;
			if (filters.length > 0) d.filters = filters;
		});
	}

	protected pickerVisible = signal(false);
	protected pickerCategories = signal<FileCategory[]>([]);
	protected pickerCurrentValue = signal('');
	private pickerTarget: 'src' | 'fontFamily' = 'src';

	protected readonly typeOptions = [
		'TEXT', 'IMAGE', 'VIDEO', 'WEBCAM', 'BOX', 'ADDRESS', 'DATETIME', 'COUNTDOWN', 'CALENDAR', 'SCREENSHOT',
	].map(value => ({ label: humanizeEnum(value), value }));
	protected readonly srcTypeOptions = ['RELATIVE', 'ABSOLUTE']
		.map(value => ({ label: humanizeEnum(value), value }));
	protected readonly alignOptions = [
		{ label: 'left', value: 'left' },
		{ label: 'center', value: 'center' },
		{ label: 'right', value: 'right' },
	];
	protected readonly fontWeightOptions = [
		{ label: 'normal', value: 'normal' },
		{ label: 'bold', value: 'bold' },
	];
	protected readonly fontStyleOptions = [
		{ label: 'normal', value: 'normal' },
		{ label: 'italic', value: 'italic' },
	];
	protected readonly fitToOptions = [
		{ label: '---', value: '' },
		{ label: 'width', value: 'width' },
		{ label: 'height', value: 'height' },
		{ label: 'size', value: 'size' },
		{ label: 'auto', value: 'auto' },
		{ label: 'auto2', value: 'auto2' },
	];
	protected readonly fitToOptionsPerspective = [
		{ label: 'size', value: 'size' },
		{ label: 'auto', value: 'auto' },
	];
	protected readonly placementTypeOptions = [{ label: humanizeEnum('PERSPECTIVE_TRANSFORM'), value: 'PERSPECTIVE_TRANSFORM' }];

	/** Legacy NORMAL items keep showing their real (now unselectable) value instead of a blank dropdown. */
	protected placementTypeOptionsFor(type: string): { label: string; value: string }[] {
		return type === 'PERSPECTIVE_TRANSFORM' ? this.placementTypeOptions : [{ label: humanizeEnum(type), value: type }];
	}
	protected readonly webcamModeOptions = ['PHOTO', 'VIDEO']
		.map(value => ({ label: humanizeEnum(value), value }));

	protected fileName(url: string | undefined): string {
		if (!url) return '';
		if (url.startsWith('https://')) return url;
		const media = this.filesService.findByBaseUrl(url);
		return media?.name ?? url.split('/').pop() ?? url;
	}

	protected colorToHex(val: string | undefined): string {
		return (val ?? '#000000').replace('#', '');
	}

	protected hexToColor(hex: string): string {
		return hex.startsWith('#') ? hex : `#${hex}`;
	}

	 
	protected patch(patcher: (draft: any) => void): void {
		const clone = cloneDeep(this.item() ?? {});
		patcher(clone);
		this.itemChange.emit(clone);
	}

	protected setVar(value: string): void {
		this.patch(d => {
			if (value) d.source.dataVariable = value;
			else delete d.source.dataVariable;
		});
	}

	protected setName(value: string): void {
		this.patch(d => {
			if (value) d.name = value;
			else delete d.name;
		});
	}

	protected setAttributes(attrs: Record<string, unknown>): void {
		this.patch(d => {
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				d.attributes = attrs;
				d.groups = keys;
			} else {
				delete d.attributes;
				delete d.groups;
			}
		});
	}

	protected setText(value: string): void {
		this.patch(d => {
			if (!d.source.data) d.source.data = {};
			d.source.data.text = value || null;
		});
	}

	protected setDataSrc(value: string): void {
		this.patch(d => {
			if (!d.source.data) d.source.data = {};
			d.source.data.src = value;
			d.source.srcType = 'ABSOLUTE';
		});
	}

	protected clearField(field: string): void {
		this.patch(d => {
			if (d.source?.data?.[field]) delete d.source.data[field];
		});
	}

	protected setDataMode(value: string): void {
		this.patch(d => {
			if (!d.source.data) d.source.data = {};
			d.source.data.mode = value;
		});
	}

	/**
	 * An absent fitTo means "stretch", not "size" — return null so the select
	 * shows its placeholder instead of a value that isn't stored. Stored values
	 * come in either case; the option values are lower-case.
	 */
	protected fitToValue(placement: Placement | undefined): string | null {
		return placement?.fitTo ? placement.fitTo.toLowerCase() : null;
	}

	protected setFitTo(value: string): void {
		this.patch(d => {
			if (!d.placement) d.placement = {};
			if (value) d.placement.fitTo = value;
			else delete d.placement.fitTo;
		});
	}

	protected setSize(field: 'width' | 'height', val: number | null): void {
		this.patch(d => {
			if (!d.placement) d.placement = {};
			if (!d.placement.fixedSize) d.placement.fixedSize = {};
			if (val != null) d.placement.fixedSize[field] = val;
		});
	}

	protected setPos(field: 'top' | 'left', val: number | null): void {
		this.patch(d => {
			if (!d.placement) d.placement = {};
			if (val != null) d.placement[field] = val;
			else delete d.placement[field];
		});
	}

	protected setFrameField(field: 'startFrame' | 'endFrame', val: number | null): void {
		this.patch(d => {
			if (val != null) d[field] = val;
			else delete d[field];
		});
	}

	protected setTextDataField(field: string, value: string | number | boolean): void {
		this.patch(d => {
			if (!d.source.data) d.source.data = {};
			d.source.data[field] = value;
		});
	}

	/** DEV-701: text renders in two phases — `align` (lower-case) lines up
	 * lines within the text block, `hPosition` (upper-case) positions that
	 * block within its placement box. Single UI control must always write
	 * both so single-line text (where `align` alone is a no-op) still moves. */
	private readonly hPositionByAlign: Record<string, 'LEFT' | 'CENTER' | 'RIGHT'> = {
		left: 'LEFT',
		center: 'CENTER',
		right: 'RIGHT',
	};

	protected setAlign(value: string): void {
		this.patch(d => {
			if (!d.source.data) d.source.data = {};
			d.source.data.align = value;
			if (!d.placement) d.placement = {};
			d.placement.hPosition = this.hPositionByAlign[value] ?? 'CENTER';
		});
	}

	protected setTextDataNumber(field: string, val: number | null): void {
		this.patch(d => {
			if (!d.source.data) d.source.data = {};
			if (val != null) d.source.data[field] = val;
		});
	}

	protected setDataBool(field: string, value: boolean): void {
		this.patch(d => {
			if (!d.source.data) d.source.data = {};
			d.source.data[field] = value;
		});
	}

	protected setPlacementField(field: string, value: string): void {
		this.patch(d => {
			if (!d.placement) d.placement = {};
			d.placement[field] = value;
		});
	}

	protected setPlacementBool(field: string, value: boolean): void {
		this.patch(d => {
			if (!d.placement) d.placement = {};
			d.placement[field] = value;
		});
	}

	/** PRO-131: UI edits opacity as a 0-100 percent (matching video-editor
	 * convention, per the ticket), stored data stays the pre-existing 0-1
	 * fraction (matches what the player/backend already expect — no data
	 * format change, only the input's display). */
	protected getOpacityPercent(): number {
		return Math.round((this.item()?.opacity ?? 1) * 100);
	}

	protected setOpacityPercent(percent: number | null): void {
		this.patch(d => d.opacity = (percent ?? 100) / 100);
	}

	/** PRO-215: dynamic opacity — a repeatable list of start/end-frame +
	 * start/end-intensity keyframes (same per-row fields as the filters-editor's
	 * "Dynamic" toggle for e.g. BLUR strength, per Jitka's request), but as an
	 * add/remove/move list rather than a single on/off toggle, since a content
	 * item can need more than one fade segment (e.g. fade in, then fade out
	 * later) and the player's `opacities` field is itself an array. */
	protected expandedOpacityKeyframes = signal<ReadonlySet<number>>(new Set());

	protected isOpacityKeyframeExpanded(index: number): boolean {
		return this.expandedOpacityKeyframes().has(index);
	}

	protected toggleOpacityKeyframeExpanded(index: number): void {
		const next = new Set(this.expandedOpacityKeyframes());
		if (next.has(index)) next.delete(index);
		else next.add(index);
		this.expandedOpacityKeyframes.set(next);
	}

	protected addOpacityKeyframe(): void {
		const newIndex = this.item()?.opacities?.length ?? 0;
		this.patch(d => {
			if (!Array.isArray(d.opacities)) d.opacities = [];
			d.opacities.push({});
		});
		const next = new Set(this.expandedOpacityKeyframes());
		next.add(newIndex);
		this.expandedOpacityKeyframes.set(next);
	}

	protected removeOpacityKeyframe(index: number): void {
		this.patch(d => {
			if (!Array.isArray(d.opacities)) return;
			d.opacities.splice(index, 1);
			if (d.opacities.length === 0) delete d.opacities;
		});
		const next = new Set<number>();
		for (const i of this.expandedOpacityKeyframes()) {
			if (i < index) next.add(i);
			else if (i > index) next.add(i - 1);
		}
		this.expandedOpacityKeyframes.set(next);
	}

	protected moveOpacityKeyframe(index: number, dir: -1 | 1): void {
		this.patch(d => {
			const arr = d.opacities;
			if (!Array.isArray(arr)) return;
			const j = index + dir;
			if (j < 0 || j >= arr.length) return;
			[arr[index], arr[j]] = [arr[j], arr[index]];
		});
		const j = index + dir;
		const cur = this.expandedOpacityKeyframes();
		const hadIndex = cur.has(index);
		const hadJ = cur.has(j);
		if (hadIndex !== hadJ) {
			const next = new Set(cur);
			if (hadIndex) { next.delete(index); next.add(j); }
			else { next.delete(j); next.add(index); }
			this.expandedOpacityKeyframes.set(next);
		}
	}

	protected getOpacityKeyframeFrame(index: number, field: 'startFrame' | 'endFrame'): number | null {
		const v = this.item()?.opacities?.[index]?.[field];
		return typeof v === 'number' ? v : null;
	}

	protected setOpacityKeyframeFrame(index: number, field: 'startFrame' | 'endFrame', value: number | null): void {
		this.patch(d => {
			if (!d.opacities?.[index]) return;
			if (value != null) d.opacities[index][field] = value;
			else delete d.opacities[index][field];
		});
	}

	protected getOpacityKeyframePercent(index: number, field: 'startOpacity' | 'endOpacity'): number | null {
		const v = this.item()?.opacities?.[index]?.[field];
		return typeof v === 'number' ? Math.round(v * 100) : null;
	}

	protected setOpacityKeyframePercent(index: number, field: 'startOpacity' | 'endOpacity', percent: number | null): void {
		this.patch(d => {
			if (!d.opacities?.[index]) return;
			if (percent != null) d.opacities[index][field] = percent / 100;
			else delete d.opacities[index][field];
		});
	}

	protected openSrcPicker(): void {
		const dt = this.item()?.source?.dataType;
		this.pickerCategories.set(dt === 'IMAGE' ? ['images'] : ['video']);
		this.pickerCurrentValue.set(this.item()?.source?.data?.src ?? '');
		this.pickerTarget = 'src';
		this.pickerVisible.set(true);
	}

	protected openFontPicker(): void {
		this.pickerCategories.set(['fonts']);
		this.pickerCurrentValue.set(this.item()?.source?.data?.fontFamily ?? '');
		this.pickerTarget = 'fontFamily';
		this.pickerVisible.set(true);
	}

	protected onPickerSelected(value: string): void {
		if (this.pickerTarget === 'src') {
			this.setDataSrc(value);
		} else {
			this.setTextDataField('fontFamily', value);
		}
	}

	protected initCoords(): void {
		this.patch(d => {
			if (!d.placement) d.placement = {};
			d.placement.coords = [];
		});
	}

	// ── Corner-pin coords editing (DEV-308 — mirrors scene-form's actions
	// pattern: discrete labelled number fields for the editable static case,
	// a read-only hover-popover summary otherwise, instead of a raw-JSON
	// textarea). ────────────────────────────────────────────────────────────
	protected readonly cornerLabels: ReadonlyArray<{ index: number; label: string }> = [
		{ index: 0, label: 'Upper Left' },
		{ index: 1, label: 'Upper Right' },
		{ index: 2, label: 'Lower Left' },
		{ index: 3, label: 'Lower Right' },
	];

	protected getCoordAxis(index: number, axis: 'x' | 'y'): number | null {
		return this.item()?.placement?.coords?.[index]?.[axis] ?? null;
	}

	protected setCoordAxis(index: number, axis: 'x' | 'y', value: number | null): void {
		this.patch(d => {
			if (!d.placement) d.placement = {};
			if (!Array.isArray(d.placement.coords) || d.placement.coords.length !== 4) {
				d.placement.coords = [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }];
			}
			d.placement.coords[index][axis] = value ?? 0;
			delete d.placement.frames; // coords/frames mutually exclusive — same invariant as applyAeConversion below
		});
	}

	protected hasPlacementCoords(): boolean {
		const p = this.item()?.placement;
		return !!(p?.coords?.length || (p?.frames && Object.keys(p.frames).length > 0));
	}

	protected placementFrameCount(): number {
		const frames = this.item()?.placement?.frames;
		return frames ? Object.keys(frames).length : 0;
	}

	/** Read-only summary table for the coords popover — dynamic mode has no
	 * manual per-frame editing UI (matches scene-form's actions: frames are
	 * populated via AE import only, inspected via this same kind of hover
	 * summary, not hand-edited field by field). */
	protected getPlacementCoordsSummary(): string {
		const p = this.item()?.placement as Placement | undefined;
		const cellsFor = (coords: PlacementCoord[] | undefined): string =>
			(coords ?? []).map(c => `<td>${c.x.toFixed(1)}, ${c.y.toFixed(1)}</td>`).join('');
		if (p?.frames && Object.keys(p.frames).length > 0) {
			const entries = Object.entries(p.frames).sort((a, b) => Number(a[0]) - Number(b[0]));
			const rows = entries.map(([f, v]) => `<tr><td>${f}</td>${cellsFor(v.coords)}</tr>`).join('');
			return `<table class="ae-tip-table"><thead><tr><th>Frame</th><th>UL</th><th>UR</th><th>LL</th><th>LR</th></tr></thead><tbody>${rows}</tbody></table>`;
		}
		if (p?.coords?.length) {
			return `<table class="ae-tip-table"><thead><tr><th>UL</th><th>UR</th><th>LL</th><th>LR</th></tr></thead><tbody><tr>${cellsFor(p.coords)}</tr></tbody></table>`;
		}
		return '';
	}

	protected clearPlacementCoords(): void {
		this.patch(d => {
			if (!d.placement) return;
			delete d.placement.coords;
			delete d.placement.frames;
		});
	}

	// ── AE Corner Pin Import (DEV-308 — inline expand/collapse pattern
	// shared with scene-form's actions, instead of this component's own
	// modal dialog. Computation unchanged: still the full 4-corner
	// toWeboutStatic/toWeboutDynamic, never the actions' rectangle
	// simplification — content items keep true perspective-transform data.) ──
	protected aeInputOpen = signal(false);
	protected aeImportStatus = signal<{ type: 'ok' | 'error'; message: string } | null>(null);
	private aeStatusTimer: ReturnType<typeof setTimeout> | null = null;

	private setAeStatus(type: 'ok' | 'error', message: string): void {
		if (this.aeStatusTimer !== null) clearTimeout(this.aeStatusTimer);
		this.aeImportStatus.set({ type, message });
		this.aeStatusTimer = setTimeout(() => this.aeImportStatus.set(null), 4000);
	}

	protected openAeInput(): void {
		this.aeInputOpen.set(true);
		setTimeout(() => {
			(document.querySelector('textarea[data-ae-input="placement"]') as HTMLTextAreaElement | null)?.focus();
		}, 0);
	}

	protected closeAeInput(): void {
		this.aeInputOpen.set(false);
	}

	private processAeImport(text: string): boolean {
		if (!text.trim()) {
			this.setAeStatus('error', 'No text to parse');
			return false;
		}
		const parsed = parseAeCornerPin(text);
		if (!parsed.upperLeft.length || !parsed.upperRight.length || !parsed.lowerLeft.length || !parsed.lowerRight.length) {
			this.setAeStatus('error', 'Could not parse all 4 corners — check the input format');
			return false;
		}

		// Detect static vs. dynamic from the pasted data itself (how many
		// frames all 4 corners share), same as scene-form's action import —
		// rather than trusting whatever the "Content Type" select happened to
		// be set to beforehand, which could silently mismatch the actual data.
		const dynamicResult = toWeboutDynamic(parsed);
		const frameCount = Object.keys(dynamicResult).length;
		if (frameCount > 1) {
			const firstFrame = Object.keys(dynamicResult).map(Number).sort((a, b) => a - b)[0];
			const [ul, ur, ll] = dynamicResult[String(firstFrame)].coords;
			const rect = cornerPinToButtonRect({ upperLeft: ul, upperRight: ur, lowerLeft: ll });
			this.patch(d => {
				if (!d.placement) d.placement = {};
				d.placement.contentType = 'DYNAMIC';
				d.placement.frames = dynamicResult;
				d.placement.fixedSize = { width: Math.round(rect.width), height: Math.round(rect.height) };
				delete d.placement.coords; // only frames OR coords, never both
			});
			this.setAeStatus('ok', `${frameCount} keyframes imported — size ${Math.round(rect.width)}×${Math.round(rect.height)} (Dynamic)`);
		} else {
			const result = toWeboutStatic(parsed);
			const rect = cornerPinToButtonRect({ upperLeft: result[0], upperRight: result[1], lowerLeft: result[2] });
			this.patch(d => {
				if (!d.placement) d.placement = {};
				d.placement.contentType = 'STATIC';
				d.placement.coords = result;
				d.placement.fixedSize = { width: Math.round(rect.width), height: Math.round(rect.height) };
				delete d.placement.frames; // only coords OR frames, never both
			});
			this.setAeStatus('ok', `4 corners imported — size ${Math.round(rect.width)}×${Math.round(rect.height)} (Static)`);
		}
		return true;
	}

	protected async exportAeToClipboard(): Promise<void> {
		const placement = this.item()?.placement as Placement | undefined;
		if (!placement) return;
		try {
			await navigator.clipboard.writeText(toAeCornerPin(placement, this.aeComp()));
			const frameCount = placement.frames ? Object.keys(placement.frames).length : 1;
			this.setAeStatus('ok', `${frameCount} frame(s) copied — paste onto a Corner Pin effect in AE`);
		} catch {
			this.setAeStatus('error', 'Could not write to clipboard');
		}
	}

	protected onAeTextareaPaste(event: ClipboardEvent): void {
		const text = event.clipboardData?.getData('text/plain') ?? '';
		if (!text.trim()) return;
		event.preventDefault();
		if (this.processAeImport(text)) this.aeInputOpen.set(false);
	}

	protected onAeTextareaKeydown(event: KeyboardEvent, el: HTMLTextAreaElement): void {
		if (event.key === 'Escape') {
			event.preventDefault();
			this.aeInputOpen.set(false);
		} else if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
			event.preventDefault();
			if (this.processAeImport(el.value)) this.aeInputOpen.set(false);
		}
	}

	protected importAeFromTextarea(text: string): void {
		if (this.processAeImport(text)) this.aeInputOpen.set(false);
	}
}
